/* eslint-disable react-refresh/only-export-components -- deliberately a mixed module: shared tokens/helpers exported beside components; HMR full-reload is acceptable here. */
import { formatCount, formatMoney as formatOrgMoney } from '@utils/currency';
import { Text } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { type FC, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

/**
 * Shared presentational primitives for purchase-order / order surfaces.
 * Extracted from the PO Builder container so both it and the mother-order
 * ("Orders") views render status, money, dates, and form labels identically.
 */

export const textColors = {
    primary: 'rgb(26, 32, 44)',
    secondary: 'rgb(100, 116, 139)',
    muted: 'rgb(148, 163, 184)',
};

export type StatusStyle = { color: string; bg: string; label: string };

// Covers authored statuses (DRAFT, ISSUED, IN_PRODUCTION, PARTIALLY_SHIPPED,
// ...) and synced statuses (Created, Approved, At Destination, ...). All keys
// are UPPERCASE_UNDERSCORED; StatusBadge normalizes input before lookup.
export const STATUS_STYLES: Record<string, StatusStyle> = {
    DRAFT: { color: 'rgb(100, 116, 139)', bg: 'rgba(100, 116, 139, 0.12)', label: 'Draft' },
    CREATED: { color: 'rgb(100, 116, 139)', bg: 'rgba(100, 116, 139, 0.12)', label: 'Created' },
    ISSUED: { color: 'rgb(1, 149, 255)', bg: 'rgba(1, 149, 255, 0.12)', label: 'Issued' },
    APPROVED: { color: 'rgb(45, 187, 136)', bg: 'rgba(45, 187, 136, 0.14)', label: 'Approved' },
    IN_PRODUCTION: { color: 'rgb(202, 138, 4)', bg: 'rgba(219, 182, 88, 0.18)', label: 'In Production' },
    READY_TO_SHIP: { color: 'rgb(124, 95, 240)', bg: 'rgba(144, 117, 255, 0.16)', label: 'Ready to Ship' },
    PARTIALLY_SHIPPED: { color: 'rgb(124, 95, 240)', bg: 'rgba(144, 117, 255, 0.16)', label: 'Partially Shipped' },
    SHIPPED: { color: 'rgb(79, 70, 229)', bg: 'rgba(99, 102, 241, 0.16)', label: 'Shipped' },
    AT_DESTINATION: { color: 'rgb(8, 145, 178)', bg: 'rgba(8, 145, 178, 0.14)', label: 'At Destination Port' },
    INVOICE_RAISED: { color: 'rgb(202, 138, 4)', bg: 'rgba(219, 182, 88, 0.18)', label: 'Invoice Raised' },
    PAYMENT_DONE: { color: 'rgb(45, 187, 136)', bg: 'rgba(45, 187, 136, 0.14)', label: 'Payment Done' },
    PARTIALLY_RECEIVED: { color: 'rgb(202, 138, 4)', bg: 'rgba(219, 182, 88, 0.18)', label: 'Partially Received' },
    RECEIVED: { color: 'rgb(45, 187, 136)', bg: 'rgba(45, 187, 136, 0.14)', label: 'Received' },
    REJECTED: { color: 'rgb(205, 101, 101)', bg: 'rgba(205, 101, 101, 0.12)', label: 'Rejected' },
    CANCELLED: { color: 'rgb(205, 101, 101)', bg: 'rgba(205, 101, 101, 0.12)', label: 'Cancelled' },
    // Sales-order display statuses and line statuses (docs/prds/sales-orders-v1.md, section 5)
    OPEN: { color: 'rgb(100, 116, 139)', bg: 'rgba(100, 116, 139, 0.12)', label: 'Open' },
    PARTIALLY_DISPATCHED: { color: 'rgb(202, 138, 4)', bg: 'rgba(219, 182, 88, 0.18)', label: 'Partially dispatched' },
    DISPATCHED: { color: 'rgb(79, 70, 229)', bg: 'rgba(99, 102, 241, 0.16)', label: 'Dispatched' },
    FULFILLED: { color: 'rgb(45, 187, 136)', bg: 'rgba(45, 187, 136, 0.14)', label: 'Fulfilled' },
    LAPSED: { color: 'rgb(164, 81, 81)', bg: 'rgba(219, 182, 88, 0.18)', label: 'Lapsed' },
    EXPIRED: { color: 'rgb(205, 101, 101)', bg: 'rgba(205, 101, 101, 0.12)', label: 'Expired' },
    UNFULFILLED: { color: 'rgb(100, 116, 139)', bg: 'rgba(100, 116, 139, 0.12)', label: 'Unfulfilled' },
    PARTIALLY_FULFILLED: { color: 'rgb(202, 138, 4)', bg: 'rgba(219, 182, 88, 0.18)', label: 'Partially fulfilled' },
    // Payment-tranche statuses (derived; vendor-payments-v1)
    PAID: { color: 'rgb(45, 187, 136)', bg: 'rgba(45, 187, 136, 0.14)', label: 'Paid' },
    PARTIALLY_PAID: { color: 'rgb(202, 138, 4)', bg: 'rgba(219, 182, 88, 0.18)', label: 'Partially Paid' },
    OVERDUE: { color: 'rgb(205, 101, 101)', bg: 'rgba(205, 101, 101, 0.12)', label: 'Overdue' },
    DUE_SOON: { color: 'rgb(202, 138, 4)', bg: 'rgba(219, 182, 88, 0.18)', label: 'Due Soon' },
    DUE: { color: 'rgb(1, 149, 255)', bg: 'rgba(1, 149, 255, 0.12)', label: 'Due' },
    SCHEDULED: { color: 'rgb(100, 116, 139)', bg: 'rgba(100, 116, 139, 0.12)', label: 'Scheduled' },
    AWAITING_ANCHOR: { color: 'rgb(124, 95, 240)', bg: 'rgba(144, 117, 255, 0.16)', label: 'Unscheduled' },
};

export const FALLBACK_STATUS_STYLE: StatusStyle = {
    color: 'rgb(100, 116, 139)',
    bg: 'rgba(100, 116, 139, 0.12)',
    label: 'Unknown',
};

export const normalizeStatusKey = (
    status: string | null | undefined,
): string => {
    if (!status) return 'UNKNOWN';
    return String(status).trim().replace(/\s+/g, '_').toUpperCase();
};

export const STATUS_OPTIONS = [
    { value: 'DRAFT', label: 'Draft' },
    { value: 'ISSUED', label: 'Issued' },
    { value: 'IN_PRODUCTION', label: 'In Production' },
    { value: 'READY_TO_SHIP', label: 'Ready to Ship' },
    { value: 'PARTIALLY_SHIPPED', label: 'Partially Shipped' },
    { value: 'SHIPPED', label: 'Shipped' },
    { value: 'AT_DESTINATION', label: 'At Destination Port' },
    { value: 'PARTIALLY_RECEIVED', label: 'Partially Received' },
    { value: 'RECEIVED', label: 'Received' },
    { value: 'CANCELLED', label: 'Cancelled' },
];

export const CURRENCY_OPTIONS = [
    { value: 'INR', label: 'INR (₹)' },
    { value: 'USD', label: 'USD ($)' },
    { value: 'EUR', label: 'EUR (€)' },
    { value: 'GBP', label: 'GBP (£)' },
    { value: 'AED', label: 'AED (د.إ)' },
    { value: 'CNY', label: 'CNY (¥)' },
    { value: 'SGD', label: 'SGD (S$)' },
    { value: 'JPY', label: 'JPY (¥)' },
];

export function StatusBadge({
    status,
    styleOverride,
}: {
    status: string | null | undefined;
    /**
     * A page whose statuses mean something different from the order lifecycle above (e.g. a
     * stock transfer's DISPATCHED) passes its own colour and label, drawn from this palette,
     * and keeps the same chip shape as every other grid.
     */
    styleOverride?: StatusStyle;
}) {
    const key = normalizeStatusKey(status);
    const s = styleOverride ?? STATUS_STYLES[key] ?? FALLBACK_STATUS_STYLE;
    const fallbackLabel = (status ?? 'Unknown').toString().replace(/_/g, ' ');
    const label = styleOverride?.label ?? STATUS_STYLES[key]?.label ?? fallbackLabel;
    return (
        <span
            className="inline-block rounded px-2 py-0.5"
            style={{
                backgroundColor: s.bg,
                color: s.color,
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.05em',
            }}
        >
            {label}
        </span>
    );
}

/**
 * Money the org's way - `₹31,28,96,850.00` for an INR org, `AED 312,896,850.00` for AED.
 *
 * This used to hard-code en-US grouping "per product convention", which left the same rupee
 * figure grouped one way on this screen and another on the PDF it printed. The convention is
 * now the organisation's currency, everywhere; this is the one formatter, re-exported so the
 * screens that imported it from here keep working.
 */
export function formatMoney(
    value: number | string | null | undefined,
    currency: string | null | undefined,
): string {
    return formatOrgMoney(value, currency);
}

export const MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export function formatDateDisplay(iso: string): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return `${String(d.getDate()).padStart(2, '0')}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
}

export const todayIso = (): string => new Date().toISOString().slice(0, 10);

export type LineDraft = {
    sku: string;
    description: string;
    quantity: number | '';
    unitPrice: number | '';
    /** The line's GST slab. '' is "nobody stated one", kept apart from an explicit 0%. */
    gstRate: number | '';
    materialId: string | null;
    productId: string | null;
    /** The line's goods-ready date (yyyy-MM-dd), on orders that track one. '' = not stated. */
    expectedReadinessDate?: string;
    /** The vendor has no agreed price on file for this item, so the rate is being typed, not confirmed. */
    noAgreedPrice?: boolean;
    /**
     * The form filled this price in from the vendor's agreed prices, so it follows the line's
     * quantity and the order's terms and currency. Typing a price turns it off for good; lines
     * loaded from a saved order or a CSV never have it.
     */
    priceAuto?: boolean;
    /** Shown under the price only when something needs acting on (below minimum, other currency). */
    priceNote?: string;
    /** The note needs attention (quantity under the vendor's minimum). */
    priceWarn?: boolean;
    /** Which agreed line the price is ("90-day credit · from 200"), as the price box's tooltip. */
    priceDetail?: string;
};

export type ChargeDraft = {
    label: string;
    amount: number | '';
};

export const emptyLine = (): LineDraft => ({
    sku: '',
    description: '',
    quantity: '',
    unitPrice: '',
    gstRate: '',
    materialId: null,
    productId: null,
});

export const emptyCharge = (): ChargeDraft => ({ label: '', amount: '' });

/**
 * Folds CSV rows into the lines already on the form.
 *
 * ONE LINE PER SKU is the rule everywhere on an order: a row for a SKU that is already there
 * adds to that line's quantity rather than opening a second line, and two rows for one SKU in
 * the file become one. Blank drafts (the untouched first row) are dropped on the way. Returns
 * how many rows were folded in, so the form can say so.
 */
export function mergeImportedLines(
    existing: LineDraft[],
    imported: LineDraft[],
): { lines: LineDraft[]; mergedRows: number } {
    const lines = existing.filter(
        (l) => l.sku || l.description || l.quantity !== '' || l.unitPrice !== '',
    );
    let mergedRows = 0;
    for (const row of imported) {
        const at = row.sku ? lines.findIndex((l) => l.sku === row.sku) : -1;
        if (at === -1) {
            lines.push(row);
            continue;
        }
        const current = lines[at];
        lines[at] = {
            ...current,
            quantity: (Number(current.quantity) || 0) + (Number(row.quantity) || 0),
        };
        mergedRows += 1;
    }
    return { lines, mergedRows };
}

export type VendorOption = { vendorId: string; name: string };

export type SkuOption = {
    sku: string;
    name: string;
    kind: 'product' | 'material';
    id: string;
    /** Catalog GST slab and HSN, so picking an item seeds its line rate. */
    gstRate?: number | null;
    hsnCode?: string | null;
    /** Per-unit volume in CBM. Products only; null when not on file. Materials never carry one. */
    volumeCbm?: number | null;
    /** Units in one case, so the line can be typed in cases. Products only; null = units only. */
    unitsPerCase?: number | null;
};

// Side-panel section/field styles — same tokens as the Suppliers pane so
// every drawer reads identically (section label / label-over-value fields).
export const panelSectionLabel: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: 'rgb(100, 116, 139)',
    marginBottom: 12,
};

export const panelFieldLabel: React.CSSProperties = {
    fontSize: 12,
    color: 'rgb(100, 116, 139)',
    marginBottom: 2,
};

export const panelFieldValue: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 500,
    color: 'rgb(26, 32, 44)',
};

export function PanelField({
    label,
    value,
    flex = 1,
}: {
    label: string;
    value: React.ReactNode;
    flex?: number;
}) {
    return (
        <div style={{ flex }}>
            <div style={panelFieldLabel}>{label}</div>
            <div style={panelFieldValue}>{value}</div>
        </div>
    );
}

/**
 * A field's name, and whether it has to be filled.
 *
 * `required` is opt-in rather than the default, because on these forms it is the exception:
 * the production-order modal shows around twenty inputs and exactly three of them gate the
 * submit button. Marking those three tells the reader where to look; marking everything would
 * tell them nothing.
 *
 * The asterisk is hidden from screen readers and paired with real words, because "star" read
 * aloud is not an instruction.
 */
export function FieldLabel({
    children,
    required = false,
}: {
    children: React.ReactNode;
    required?: boolean;
}) {
    return (
        <Text size="11px" fw={600} c={textColors.secondary} mb={4}>
            {children}
            {required && (
                <>
                    <Text component="span" c="red" aria-hidden="true">
                        {' '}
                        *
                    </Text>
                    <span className="sr-only"> (required)</span>
                </>
            )}
        </Text>
    );
}

/**
 * A band of the form, named.
 *
 * `first` drops the top margin. The 20px exists to SEPARATE one section from the last, so on
 * the opening section it separates nothing and simply stacks onto the pane's own 28px padding
 * - 48px of nothing before the form starts, while every later section gets 20. Opt-in rather
 * than a :first-child rule, because these headers are not always the first child of their
 * container and a CSS guess would be right in one form and wrong in the next.
 */
export function SectionHeader({
    children,
    first = false,
}: {
    children: React.ReactNode;
    first?: boolean;
}) {
    return (
        <Text
            size="11px"
            fw={700}
            c={textColors.secondary}
            mt={first ? 0 : 20}
            mb={8}
            style={{ letterSpacing: '0.06em' }}
        >
            {children}
        </Text>
    );
}

// ─── "Raise PO" from a work order ───────────────────────────────────────────

/**
 * A work order's material deficit, handed to the purchase-order form. The work order page opens
 * the form with ?workOrderId=&workOrderCode=&materialId=&materialName=&qty=&unit=; the form
 * starts with that one material line and sends `workOrderId`, so the saved order is pegged to
 * the work order.
 */
export type WorkOrderPrefill = {
    workOrderId: string;
    workOrderCode: string;
    materialId: string;
    materialName: string;
    qty: number;
    unit?: string;
};

/**
 * The catalog a form is given while it loads. One array, not a fresh `[]` per render: the form
 * resets when its catalog changes, and a new empty list every render reads as a change.
 */
export const NO_SKU_OPTIONS: SkuOption[] = [];

const WORK_ORDER_PREFILL_PARAMS = ['workOrderId', 'workOrderCode', 'materialId', 'materialName', 'qty', 'unit'];

/**
 * Reads the hand-off from the URL once, calls `onArrive` (open the form), and clears it from the
 * URL so a refresh does not start a second order.
 */
export const useWorkOrderPrefill = (onArrive: () => void) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [prefill, setPrefill] = useState<WorkOrderPrefill | null>(null);

    useEffect(() => {
        const workOrderId = searchParams.get('workOrderId');
        const materialId = searchParams.get('materialId');
        const materialName = searchParams.get('materialName');
        const qty = searchParams.get('qty');
        if (!workOrderId || !materialId || !materialName || !qty) return;

        setPrefill({
            workOrderId,
            workOrderCode: searchParams.get('workOrderCode') ?? workOrderId,
            materialId,
            materialName,
            qty: Number(qty) || 0,
            unit: searchParams.get('unit') ?? undefined,
        });
        onArrive();

        const next = new URLSearchParams(searchParams);
        for (const key of WORK_ORDER_PREFILL_PARAMS) next.delete(key);
        setSearchParams(next, { replace: true });
        // onArrive is a fresh closure every render; the hand-off is driven by the URL alone.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, setSearchParams]);

    return [prefill, setPrefill] as const;
};

/** The order's one line, as picking that material in the form would make it. */
export const workOrderPrefillLine = (prefill: WorkOrderPrefill, skuOptions: SkuOption[]): LineDraft => {
    const match = skuOptions.find((o) => o.kind === 'material' && o.id === prefill.materialId);
    return {
        ...emptyLine(),
        sku: match?.sku ?? '',
        description: match?.name ?? prefill.materialName,
        quantity: prefill.qty,
        gstRate: match?.gstRate ?? '',
        materialId: prefill.materialId,
        // Priced from the vendor's agreed prices once a vendor is picked, like a picked line.
        priceAuto: true,
    };
};

export const WorkOrderPrefillBanner: FC<{ prefill: WorkOrderPrefill }> = ({ prefill }) => (
    <div className="mb-3 rounded-md bg-sky-50 px-3 py-2">
        <Text size="13px" c={textColors.primary}>
            For Work Order{' '}
            <Text component="span" fw={600}>
                {prefill.workOrderCode}
            </Text>
            {' · '}
            {formatCount(prefill.qty)} {prefill.unit ?? 'units'} of {prefill.materialName}
            {' · '}
            <Text component="span" c={textColors.secondary}>
                pegged on save
            </Text>
        </Text>
    </div>
);

/** After the order is saved: back to the work order, which now shows it as supply. */
export const useReturnToWorkOrder = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    return (workOrderId: string) => {
        void queryClient.invalidateQueries({ queryKey: ['work-order', workOrderId] });
        void queryClient.invalidateQueries({ queryKey: ['work-order-pegged-pos', workOrderId] });
        void navigate(`/work-orders/${workOrderId}`);
    };
};
